/**
 * The engine's own quaternion routines, which are not three.js's.
 *
 * Four of these differ from the obvious implementation in ways that matter, so they are
 * reproduced rather than delegated. `angles`/`setAngles` in particular are NOT inverses of
 * each other in the original — they disagree about which component carries which axis — and
 * the game uses both. Reproducing that faithfully is the point; "fixing" it would silently
 * change every ball spin and every body angle.
 *
 * Source: base/math/quaternion.cpp.
 */

import { Quaternion, Vector3 } from 'three';
import { clamp } from './math';

/**
 * `Quaternion::GetAngles` (quaternion.cpp:197-226) — the Z Euler angle, which is the one the
 * game reads almost everywhere.
 *
 * TWO TRAPS, both of which the summary specification gets wrong and both of which change
 * every body angle in the game if missed:
 *
 *   1. THE AXIS REMAP. The routine feeds its formulas the quaternion components in the
 *      order (x, z, y, w), not (x, y, z, w). Spelled out below in raw component terms so
 *      the remap cannot be mis-applied.
 *   2. THE DECOMPOSITION ORDER is q = Rx * Ry * Rz, with Z INNERMOST — verified
 *      numerically against the engine's own quaternion product, not assumed. So POST-
 *      multiplying a Z rotation adds exactly that angle to the result, while PRE-
 *      multiplying one barely moves it unless the X and Y angles are near zero. The
 *      engine pre-multiplies in several places, and this is why those places do not do
 *      quite what their names suggest.
 */
export function zEuler(q: Quaternion): number {
  const singularityTest = q.x * q.z + q.y * q.w;
  if (singularityTest > 0.49999 || singularityTest < -0.49999) {
    // BUG, REPRODUCED NOT FIXED. The reference formula is 2*atan2(x, w); the original
    // substitutes the remapped "z" component for w. At one pole the two agree, at the
    // other the sign of the heading is flipped. Unreachable on the shipped clips — no
    // body keyframe in any of the 296 files comes near the singularity — but reproduced
    // so a rewrite that authors new clips inherits the same behaviour rather than a
    // silently different one.
    //
    // The remapped "z" is the RAW y component (the routine reads its arguments in the order
    // x, z, y, w). This read the raw z instead, which reproduced neither the original's
    // behaviour nor the correct one but a third thing. Still unreachable — the engine's own
    // clamp on spin keeps the test below 0.4998 against a threshold of 0.49999 — so nothing
    // observable changes today.
    return singularityTest > 0 ? 2 * Math.atan2(q.x, q.y) : -2 * Math.atan2(q.x, q.y);
  }
  return Math.atan2(2 * q.z * q.w - 2 * q.x * q.y, 1 - 2 * q.z * q.z - 2 * q.y * q.y);
}

/** The full three-angle extraction. */
export function angles(q: Quaternion, out = new Vector3()): Vector3 {
  const singularityTest = q.x * q.z + q.y * q.w;
  if (singularityTest > 0.49999 || singularityTest < -0.49999) {
    return out.set(0, singularityTest > 0 ? Math.PI * 0.5 : -Math.PI * 0.5, zEuler(q));
  }
  return out.set(
    // The original does not clamp before asin; a value a hair over 1 would give NaN.
    Math.atan2(2 * q.x * q.w - 2 * q.z * q.y, 1 - 2 * q.x * q.x - 2 * q.y * q.y),
    Math.asin(clamp(2 * q.x * q.z + 2 * q.y * q.w, -1, 1)),
    zEuler(q),
  );
}

/**
 * `Quaternion::SetAngles` (quaternion.cpp:228-245) — build a quaternion from three angles.
 *
 * NOT the inverse of `angles` above. The extraction reads the Y angle off the z component
 * and the Z angle off the y component; this writes them the other way round. Both are used
 * on the ball's spin within a single tick, so the asymmetry is live, not theoretical.
 */
export function setAngles(x: number, y: number, z: number, out = new Quaternion()): Quaternion {
  const c1 = Math.cos(y / 2);
  const s1 = Math.sin(y / 2);
  const c2 = Math.cos(z / 2);
  const s2 = Math.sin(z / 2);
  const c3 = Math.cos(x / 2);
  const s3 = Math.sin(x / 2);
  const c1c2 = c1 * c2;
  const s1s2 = s1 * s2;
  return out.set(
    c1c2 * s3 + s1s2 * c3,
    s1 * c2 * c3 + c1 * s2 * s3,
    c1 * s2 * c3 - s1 * c2 * s3,
    c1c2 * c3 - s1s2 * s3,
  );
}

/** `Quaternion::GetRotationTo` — the rotation taking `from` onto `to`. */
export function rotationTo(from: Quaternion, to: Quaternion, out = new Quaternion()): Quaternion {
  return out.copy(to).multiply(from.clone().invert());
}

/**
 * `Quaternion::GetRotationAngle` (quaternion.hpp:75-77) — the angle between two
 * orientations, in radians.
 *
 * No absolute value on the dot product, so two orientations that are the same rotation
 * expressed with opposite signs report a full turn apart rather than nothing apart. That is
 * the original's, and the ball's spin coupling is calibrated against it.
 */
export function rotationAngle(q: Quaternion, reference: Quaternion): number {
  const dot = q.x * reference.x + q.y * reference.y + q.z * reference.z + q.w * reference.w;
  return 2 * Math.acos(clamp(dot, -1, 1));
}

/** `Quaternion::GetRotationMultipliedBy` — scale a rotation's ANGLE, keeping its axis. */
export function rotationMultipliedBy(
  q: Quaternion,
  factor: number,
  out = new Quaternion(),
): Quaternion {
  const normalised = q.clone().normalize();
  let angle = 2 * Math.acos(clamp(normalised.w, -1, 1));
  const sinHalf = Math.sqrt(Math.max(0, 1 - normalised.w * normalised.w));
  const axis =
    sinHalf < 1e-6
      ? new Vector3(0, 0, 1)
      : new Vector3(normalised.x / sinHalf, normalised.y / sinHalf, normalised.z / sinHalf);
  if (angle > Math.PI) angle -= 2 * Math.PI;
  angle = (angle * factor) % (2 * Math.PI);
  return out.setFromAxisAngle(axis, angle).normalize();
}

/** `Quaternion::MakeSameNeighborhood` — flip `q` into `reference`'s hemisphere, return the dot. */
export function makeSameNeighborhood(q: Quaternion, reference: Quaternion): number {
  const dot = q.x * reference.x + q.y * reference.y + q.z * reference.z + q.w * reference.w;
  if (dot < 0) {
    q.set(-q.x, -q.y, -q.z, -q.w);
    return -dot;
  }
  return dot;
}

/** `Quaternion::SetAngleAxis`. Present so call sites read like the C++ they came from. */
export function angleAxis(angle: number, axis: Vector3, out = new Quaternion()): Quaternion {
  return out.setFromAxisAngle(axis.clone().normalize(), angle);
}
